/**
 * RentCafe Chatbot Block
 *
 * Injection block that loads the RentCafe chatbot embed snippet stored on the
 * property. Appends the configured script tag to document head.
 */

import { useEffect, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import type { BlockDefinition, BlockProps } from '../../types';

const INJECTED_NODE_ATTRIBUTE = 'data-rentcafe-chatbot-node';
const BUTTON_BACKGROUND_COLOR_PATTERN = /([?&]buttonbgcolor=)([^&"'`\s>]+)/i;

type RentCafeChatbotState = {
  nodes: Node[];
  value: string | null;
};

function normalizeValue(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function applyBackgroundColorOverride(embedScript: string | null, backgroundColor: string | null) {
  if (!embedScript || !backgroundColor) return embedScript;

  const buttonBackgroundColor = backgroundColor.replace(/^#/, '');
  return embedScript.replace(BUTTON_BACKGROUND_COLOR_PATTERN, `$1${buttonBackgroundColor}`);
}

function createScriptNode(originalScript: HTMLScriptElement) {
  const script = document.createElement('script');

  Array.from(originalScript.attributes).forEach(({ name, value }) => {
    script.setAttribute(name, value);
  });

  script.textContent = originalScript.textContent;
  script.setAttribute(INJECTED_NODE_ATTRIBUTE, 'true');
  return script;
}

function createHeadNodes(embedScript: string) {
  const normalized = normalizeValue(embedScript);
  if (!normalized) return [];

  const template = document.createElement('template');
  template.innerHTML = normalized;

  return Array.from(template.content.childNodes).flatMap(node => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      return text ? [document.createTextNode(text)] : [];
    }

    if (!(node instanceof HTMLElement)) return [];
    if (node instanceof HTMLScriptElement) return [createScriptNode(node)];

    const clonedNode = node.cloneNode(true);
    if (clonedNode instanceof HTMLElement) {
      clonedNode.setAttribute(INJECTED_NODE_ATTRIBUTE, 'true');
    }

    return [clonedNode];
  });
}

function getRentCafeChatbotState() {
  const windowWithState = window as Window & {
    __rentcafeChatbotState?: RentCafeChatbotState;
  };

  // Single tab-wide instance. Once we've injected the vendor script, we
  // remember it here so subsequent mounts (StrictMode, SPA navigation, etc.)
  // skip re-injection — RentCafe paints its widget directly in <body>, and
  // re-running the script duplicates the widget.
  if (windowWithState.__rentcafeChatbotState) {
    return windowWithState.__rentcafeChatbotState;
  }

  const state: RentCafeChatbotState = {
    nodes: [],
    value: null,
  };

  windowWithState.__rentcafeChatbotState = state;
  return state;
}

function RentCafeChatbotComponent(_props: BlockProps) {
  const embedScript = useBlockToken('embed_script', {
    defaultField: 'custom_rentcafe_chatbot',
  });
  const backgroundColor = useBlockToken('background_color', {
    defaultField: 'property_primary_color',
  });
  const normalizedEmbedScript = normalizeValue(embedScript);
  const normalizedBackgroundColor = normalizeValue(backgroundColor);
  // Lock the processed script to the embed-script identity. The property's
  // primary color resolves async on mount, so deriving processedEmbedScript
  // straight from both tokens would change it across renders (null bg →
  // resolved bg) and re-trigger the injection effect. The vendor's widget
  // DOM lives in <body> outside our tracking, so the swap left a stale
  // widget behind and rendered a second on top — broken layout, lost
  // click events. Memoizing on just the embed script means a real edit
  // re-runs the effect (picking up whatever bg color is current) but a
  // bg-color-only resolution does not.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const processedEmbedScript = useMemo(
    () => applyBackgroundColorOverride(normalizedEmbedScript, normalizedBackgroundColor),
    [normalizedEmbedScript],
  );

  useEffect(() => {
    if (!processedEmbedScript) return;

    const state = getRentCafeChatbotState();

    // Load the vendor script exactly once per tab and never tear it down.
    // RentCafe creates its widget DOM directly in <body> (.rcc-app-cta-wrap,
    // the toggle button, the chat container) — outside our tracking. Tearing
    // down + re-injecting on remount or SPA navigation makes the vendor paint
    // a SECOND widget on top of the existing one (broken styles + lost click
    // events), since the original body DOM isn't ours to clean up. The
    // safe-and-simple answer is to behave like a script in index.html: inject
    // once, leave alone for the tab's lifetime. Subsequent edits to the embed
    // script (or the bg color) won't reflect until a hard refresh — an
    // acceptable trade for not duplicating the widget.
    if (state.value) return;

    const nodes = createHeadNodes(processedEmbedScript);
    if (!nodes.length) return;

    nodes.forEach(node => {
      document.head.appendChild(node);
    });
    state.value = processedEmbedScript;
    state.nodes = nodes;
  }, [processedEmbedScript]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'RentCafeChatbot',
  displayName: 'RentCafe CRM IQ',
  tokens: {
    embed_script: {
      label: 'Embed Script',
      type: 'text',
      defaultField: 'custom_rentcafe_chatbot',
      suggestedFields: ['custom_rentcafe_chatbot'],
      description: 'RentCafe chatbot embed script tag stored on the property.',
      required: true,
    },
    background_color: {
      label: 'Background Color',
      type: 'color',
      defaultField: 'property_primary_color',
      suggestedFields: ['property_primary_color'],
      description: 'Optional override for the RentCafe widget background color.',
    },
  },
};

export const rentcafeChatbotBlock: BlockDefinition = {
  id: 'rentcafe-chatbot',
  name: 'RentCafe CRM IQ',
  description: 'RentCafe CRM IQ chatbot injected from the property embed script.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: RentCafeChatbotComponent,
  contentSchema,
  tags: ['rentcafe', 'crm iq', 'chatbot', 'chat', 'widget', 'yardi'],
};
